import { Controller } from "@hotwired/stimulus"
import { matches, near } from "../lib/search_words"

// One box, two lists. It filters this activity's signups in the browser and,
// at the same time, asks Admin::CheckinCandidatesController for members who
// are not signed up yet. Picking either one goes to Admin::WalkInsController,
// which appends a row via turbo_stream.
export default class extends Controller {
  static targets = ["search", "row", "addPrompt", "addPromptName", "list", "empty", "candidates"]
  static values  = { searchUrl: String, minimumQuery: { type: Number, default: 2 } }

  connect() {
    this.filter()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
  }

  // Names are matched the way SearchWords matches the member candidates below,
  // so both lists agree: exact first, and names a typo away only when nobody
  // matches exactly. A near name is shown but does not hide the walk-in
  // button, because "Anne Jensen" walking in is not "Anna Jensen" signed up.
  filter() {
    const query = (this.searchTarget.value || "").trim()
    const phoneDigits = query.replace(/\D/g, "")

    const exact = this.rowTargets.filter(row =>
      query === "" ||
      matches(row.dataset.searchName, query) ||
      (phoneDigits.length > 0 && (row.dataset.searchPhone || "").includes(phoneDigits)))
    const shown = exact.length > 0 ? exact : this.rowTargets.filter(row => near(row.dataset.searchName, query))

    this.rowTargets.forEach(row => row.classList.toggle("hidden", !shown.includes(row)))
    this.emptyTarget.classList.toggle("hidden", shown.length > 0 || query === "")

    const showAdd = exact.length === 0 && query !== ""
    this.addPromptTarget.classList.toggle("hidden", !showAdd)
    if (showAdd) this.addPromptNameTarget.textContent = `"${this.searchTarget.value.trim()}"`

    this.searchMembers()
  }

  // Debounced: the member name is encrypted, so the server decrypts its way
  // through the members to match it. One request per pause in typing, not one
  // per keystroke.
  searchMembers() {
    if (!this.hasCandidatesTarget || !this.hasSearchUrlValue) return

    clearTimeout(this.searchTimer)
    const query = this.searchTarget.value.trim()

    if (query.length < this.minimumQueryValue) {
      this.candidatesTarget.innerHTML = ""
      // Cleared as well, so retyping the same query loads again instead of
      // leaving the emptied frame standing.
      this.candidatesTarget.removeAttribute("src")
      return
    }

    this.searchTimer = setTimeout(() => {
      this.candidatesTarget.src = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    }, 300)
  }

  addWalkIn() {
    const typed = this.searchTarget.value.trim()
    if (!typed) return

    // The same box filters by name or by phone digits, so a full number typed
    // here is a phone number, not a name. Sending it as one lets the server
    // tie the walk-in to an existing member.
    const digits = typed.replace(/\D/g, "")
    const looksLikePhone = digits.length >= 8 && /^[\d\s+]+$/.test(typed)
    const name  = looksLikePhone ? "" : typed
    const phone = looksLikePhone ? digits : ""

    const url = this.element.querySelector("[data-walk-in-url]")?.dataset?.walkInUrl || window.location.pathname.replace(/\/check-in$/, "/walk-ins")
    const token = document.querySelector("meta[name=csrf-token]")?.content

    const form = document.createElement("form")
    form.method = "post"
    form.action = url
    form.style.display = "none"
    form.innerHTML = `
      <input type="hidden" name="authenticity_token" value="${token || ""}">
      <input type="hidden" name="name" value="${name.replace(/"/g, "&quot;")}">
      <input type="hidden" name="phone" value="${phone}">
    `
    document.body.appendChild(form)
    form.requestSubmit()
  }
}
